import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Maximize2, Cloud, Thermometer, Clock } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WEBCAM_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webcam`;
const REFRESH_INTERVAL = 3 * 60 * 1000; // 3 minuts

export const WebcamCard = () => {
  const { language } = useLanguage();
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [imageKey, setImageKey] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(false);
  const [temperature, setTemperature] = useState(null);
  const [weatherDesc, setWeatherDesc] = useState('');

  const texts = {
    ca: {
      title: 'Webcam del Camp',
      subtitle: 'Estat actual en temps real',
      updated: 'Actualitzat fa',
      minutes: 'min',
      viewNow: 'Veure el camp ara',
      forecast: 'Previsió del temps',
      liveView: 'Vista en directe',
      temperature: 'Temperatura'
    },
    es: {
      title: 'Webcam del Campo',
      subtitle: 'Estado actual en tiempo real',
      updated: 'Actualizado hace',
      minutes: 'min',
      viewNow: 'Ver el campo ahora',
      forecast: 'Previsión del tiempo',
      liveView: 'Vista en directo',
      temperature: 'Temperatura'
    },
    fr: {
      title: 'Webcam du Parcours',
      subtitle: 'État actuel en temps réel',
      updated: 'Mis à jour il y a',
      minutes: 'min',
      viewNow: 'Voir le parcours',
      forecast: 'Prévisions météo',
      liveView: 'Vue en direct',
      temperature: 'Température'
    },
    en: {
      title: 'Course Webcam',
      subtitle: 'Current real-time status',
      updated: 'Updated',
      minutes: 'min ago',
      viewNow: 'View course now',
      forecast: 'Weather forecast',
      liveView: 'Live view',
      temperature: 'Temperature'
    }
  };

  const t = texts[language] || texts.ca;

  // Fetch weather data (using Open-Meteo free API for Aravell coordinates)
  const fetchWeather = useCallback(async () => {
    try {
      // Aravell Golf Club coordinates: 42.3605°N, 1.4053°E
      const response = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=42.3605&longitude=1.4053&current=temperature_2m,weather_code&timezone=Europe%2FMadrid'
      );
      const data = await response.json();
      if (data.current) {
        setTemperature(Math.round(data.current.temperature_2m));
        // Weather code to description
        const code = data.current.weather_code;
        if (code === 0) setWeatherDesc(language === 'ca' ? 'Cel clar' : language === 'es' ? 'Cielo despejado' : language === 'fr' ? 'Ciel dégagé' : 'Clear sky');
        else if (code <= 3) setWeatherDesc(language === 'ca' ? 'Parcialment ennuvolat' : language === 'es' ? 'Parcialmente nublado' : language === 'fr' ? 'Partiellement nuageux' : 'Partly cloudy');
        else if (code <= 49) setWeatherDesc(language === 'ca' ? 'Ennuvolat' : language === 'es' ? 'Nublado' : language === 'fr' ? 'Nuageux' : 'Cloudy');
        else if (code <= 69) setWeatherDesc(language === 'ca' ? 'Pluja' : language === 'es' ? 'Lluvia' : language === 'fr' ? 'Pluie' : 'Rain');
        else setWeatherDesc(language === 'ca' ? 'Variable' : language === 'es' ? 'Variable' : language === 'fr' ? 'Variable' : 'Variable');
      }
    } catch (error) {
      console.error('Weather fetch error:', error);
      setTemperature(null);
    }
  }, [language]);

  // Refresh webcam image
  const refreshImage = useCallback(() => {
    setIsLoading(true);
    setImageKey(Date.now());
    setLastUpdate(new Date());
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Calculate minutes since last update
  const getMinutesSinceUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 60000);
    return diff < 1 ? '< 1' : diff;
  };

  // Auto-refresh
  useEffect(() => {
    fetchWeather();
    const interval = setInterval(() => {
      refreshImage();
      fetchWeather();
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshImage, fetchWeather]);

  // Update minutes display
  const [minutesAgo, setMinutesAgo] = useState(getMinutesSinceUpdate());
  useEffect(() => {
    const timer = setInterval(() => {
      setMinutesAgo(getMinutesSinceUpdate());
    }, 30000);
    return () => clearInterval(timer);
  }, [lastUpdate]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-white border border-[#E5E5E5] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
        data-testid="webcam-card"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl text-[#1A1A1A] font-medium">
              {t.title}
            </h3>
            <p className="text-sm text-[#666666] mt-0.5">{t.subtitle}</p>
          </div>
          <button
            onClick={refreshImage}
            className={`p-2 text-[#666666] hover:text-[#1A4D2E] hover:bg-[#F0F2EF] rounded-full transition-colors duration-200 ${isLoading ? 'animate-spin' : ''}`}
            aria-label="Refresh"
            data-testid="webcam-refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Webcam Image */}
        <a 
          href={`${WEBCAM_URL}?t=${imageKey}`}
          target="_blank"
          rel="noopener noreferrer"
          className="relative aspect-video bg-[#F0F2EF] cursor-pointer group block"
        >
          <img
            key={imageKey}
            src={`${WEBCAM_URL}?t=${imageKey}`}
            alt="UGOLF ARAVELL Andorra - Live webcam"
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm px-4 py-2 flex items-center gap-2">
              <Maximize2 className="w-4 h-4 text-[#1A4D2E]" />
              <span className="text-sm font-medium text-[#1A4D2E]">{t.viewNow}</span>
            </div>
          </div>
          {/* Live indicator */}
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-xs text-white font-medium uppercase tracking-wider">Live</span>
          </div>
        </a>

        {/* Info Bar */}
        <div className="px-6 py-4 bg-[#F9F9F7] flex flex-wrap items-center justify-between gap-4">
          {/* Weather */}
          <div className="flex items-center gap-4">
            {temperature !== null && (
              <div className="flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-[#1A4D2E]" />
                <span className="text-lg font-semibold text-[#1A1A1A]">{temperature}°C</span>
              </div>
            )}
            {weatherDesc && (
              <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4 text-[#666666]" />
                <span className="text-sm text-[#666666]">{weatherDesc}</span>
              </div>
            )}
          </div>
          {/* Last update */}
          <div className="flex items-center gap-2 text-sm text-[#666666]">
            <Clock className="w-4 h-4" />
            <span>{t.updated} {minutesAgo} {t.minutes}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-[#E5E5E5] flex flex-wrap gap-3">
          <a
            href={`${WEBCAM_URL}?t=${imageKey}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-[140px] bg-[#1A4D2E] text-white px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase hover:bg-[#153d24] transition-colors duration-200 flex items-center justify-center gap-2"
            data-testid="webcam-view-btn"
          >
            <Maximize2 className="w-4 h-4" />
            {t.viewNow}
          </a>
          <a
            href="https://www.meteoblue.com/en/weather/week/la-seu-d'urgell_spain_3109143"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-[140px] bg-transparent border-2 border-[#1A4D2E] text-[#1A4D2E] px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase hover:bg-[#1A4D2E] hover:text-white transition-colors duration-200 flex items-center justify-center gap-2"
            data-testid="webcam-forecast-btn"
          >
            <Cloud className="w-4 h-4" />
            {t.forecast}
          </a>
        </div>
      </motion.div>
    </>
  );
};

export default WebcamCard;
